import { useEffect, useRef, useState } from 'react';
import { Box, Button, TextInput } from '@primer/react';
import { Spinner, MagnifyingGlass } from 'phosphor-react';
import { useComfyLogs } from '../hooks/useComfyLogs';

interface HTMLElementWithScroll extends HTMLDivElement {
  scrollTop: number;
  scrollHeight: number;
  clientHeight: number;
}

export const ComfyLogs = ({
  comfyUrl,
  isAccordionOpen,
}: {
  comfyUrl: string;
  isAccordionOpen: boolean;
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isUserScrolling, setIsUserScrolling] = useState(false);
  const logsContainerRef = useRef<HTMLElementWithScroll | null>(null);
  const endOfLogsRef = useRef<HTMLDivElement | null>(null);
  const endOfLogsUserScrollRef = useRef<HTMLDivElement | null>(null);

  const {
    logs,
    isReconnecting,
    isFetchingFromSSE,
    closeSSEConnection
  } = useComfyLogs({
    comfyUrl,
    enabled: isAccordionOpen
  });

  const handleScroll = () => {
    if (logsContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = logsContainerRef.current;
      if (scrollHeight - scrollTop <= clientHeight + 30) {
        setIsUserScrolling(false);
      } else {
        setIsUserScrolling(true);
      }
    }
  };

  const scrollToBottom = () => {
    setIsUserScrolling(false);
    if (endOfLogsRef.current) {
      endOfLogsRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleScrollToBottomButtonClick = () => {
    if (endOfLogsUserScrollRef.current) {
      endOfLogsUserScrollRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  useEffect(() => {
    if (!isUserScrolling && endOfLogsRef.current) {
      endOfLogsRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [logs, isAccordionOpen]);

  useEffect(() => {
    setIsUserScrolling(false);
    if (!isAccordionOpen) {
      scrollToBottom();
      closeSSEConnection();
    }
  }, [isAccordionOpen]);

  const timestampRegex = /^\[([^\]]+)\]/;

  const highlightSearchText = (text: string) => {
    if (!searchQuery) return text;
    const parts = text.split(new RegExp(`(${searchQuery})`, 'gi'));
    return parts.map((part, i) => 
      part.toLowerCase() === searchQuery.toLowerCase() ? 
        <span key={i} className="bg-yellow-500 text-black">{part}</span> : 
        part
    );
  };

  const renderLog = (log: string) => {
    const match = log.match(timestampRegex);
    if (match) {
      const matchedText = match[0];
      const remainingText = log.substring(matchedText.length).trim();
      const hasErrors = /error|Error|exception|Exception|Failed|failed|invalid|Invalid/.test(
        remainingText
      );

      return (
        <span>
          <span className="text-gray-500 mr-2">{matchedText}</span>
          <span className={`${hasErrors ? 'text-red-800' : 'text-gray-300'}`}>
            {highlightSearchText(remainingText)}
          </span>
        </span>
      );
    }
    return highlightSearchText(log);
  };

  const filteredLogs = searchQuery
    ? logs.filter(log => log.toLowerCase().includes(searchQuery.toLowerCase()))
    : logs;

  return (
    <Box
      className="relative flex flex-col overflow-x-hidden text-wrap bg-black -mb-2"
      style={{ height: 313 }}
    >
      {!logs.length ? (
        <Box className="flex justify-center my-auto h-full items-center gap-2">
          <Spinner size={20} className="animate-spin" />
          <p className="text-sm">Loading logs...</p>
        </Box>
      ) : (
        <>
          <Box className="absolute flex justify-end p-2 bg-black right-0 top-0 z-10">
            <TextInput
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search logs..."
              leadingVisual={MagnifyingGlass}
              sx={{
                width: '250px',
                backgroundColor: '#1c1c1c',
                border: '1px solid #383838',
                color: '#D7D7DB',
                '::placeholder': { color: '#6e6e6e' },
                ':focus': { borderColor: '#525252', boxShadow: 'none' }
              }}
            />
          </Box>
          <Box
            ref={logsContainerRef}
            onScroll={handleScroll}
            className="flex flex-col gap-1 overflow-y-auto bg-black px-4 py-2"
            style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}
          >
            {filteredLogs.map((log, index) => (
              <Box key={index} className="text-sm font-normal">
                {renderLog(log)}
              </Box>
            ))}

            {!isUserScrolling && isAccordionOpen ? (
              <div ref={endOfLogsRef}></div>
            ) : null}
            <div ref={endOfLogsUserScrollRef}></div>
          </Box>

          <Box className="flex justify-between mt-auto items-center w-full gap-2 text-xs text-gray-400 px-4 pt-1 pb-4 bg-black">
            {isReconnecting ? (
              <Box className="flex justify-center items-center gap-2 text-[#d5c510]">
                <Spinner size={14} className="animate-spin" />
                Reconnecting...
              </Box>
            ) : isFetchingFromSSE ? (
              <Box className="flex justify-center items-center gap-2 text-[#d5c510]">
                <Spinner size={14} className="animate-spin" />
                Listening to logs...
              </Box>
            ) : null}
            {isUserScrolling && (
              <Button
                onClick={handleScrollToBottomButtonClick}
                sx={{
                  color: '#D7D7DB',
                  fontSize: '12px',
                  padding: '0px 8px',
                  border: '1px solid #636C76',
                  borderRadius: '16px',
                }}
              >
                See Live Logs
              </Button>
            )}
          </Box>
        </>
      )}
    </Box>
  );
};